import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react';
import { Button as ActionButton } from '~/components/form/SubmitButton';
import { IconButton } from '~/components/ui/nucleus/Button/IconButton';

type SubpageProps = {
  title: string;
  isOpen: boolean;
  backTo: () => void;
  children?: React.ReactNode;
  form?: React.ReactNode;
  footer?: React.ReactNode;
  action?: {
    label: string;
    intent?: 'primary' | 'secondary';
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    type?: 'button' | 'submit';
  };
  /**
   * The HTML id of the form element when using action.type='submit'.
   * Required if action.type is 'submit' to associate the button with the form.
   */
  formId?: string;
  direction?: 'forward' | 'backward';
  onClose: () => void;
  /**
   * When provided, changes to this key will force the inner motion container
   * to unmount/remount so AnimatePresence can play exit/enter transitions
   * between different subpages while the overlay remains open.
   */
  contentKey?: string;
};

type Context = {
  backTo: () => void;
};

const SettingsSubpageContext = createContext({} as Context);

export const useSettingsContext = () => useContext(SettingsSubpageContext);

const variants = {
  forward: {
    initial: { x: '100%', opacity: 0 },
    animate: { x: '0%', opacity: 1 },
    exit: { x: '10%', opacity: 0 },
  },
  backward: {
    initial: { x: '-100%', opacity: 0 },
    animate: { x: '0%', opacity: 1 },
    exit: { x: '-10%', opacity: 0 },
  },
};

// Inner content variants so the overlay stays mounted while subpages animate
const pageVariants = {
  forward: {
    initial: { x: '100%', opacity: 0 },
    animate: { x: '0%', opacity: 1 },
    exit: { x: '-10%', opacity: 0 },
  },
  backward: {
    initial: { x: '-100%', opacity: 0 },
    animate: { x: '0%', opacity: 1 },
    exit: { x: '10%', opacity: 0 },
  },
};

export const SettingsSubpage = ({
  title,
  isOpen,
  backTo,
  children,
  form,
  footer,
  action,
  formId,
  onClose,
  direction = 'forward',
  contentKey,
}: SubpageProps) => {
  const animation = variants[direction];
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Manage global Escape key to close the dialog when open.
  // This ensures users can easily dismiss with keyboard from anywhere.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
      // Prevent Backspace/Delete from triggering browser navigation or closing
      // when focus is not inside an editable element.
      if (
        event.key === 'Backspace' ||
        event.key === 'Delete' ||
        (event as any).keyCode === 8
      ) {
        const target = event.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
          !!target &&
          ((target as HTMLElement).isContentEditable === true ||
            tag === 'INPUT' ||
            tag === 'TEXTAREA');
        if (!isEditable) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  // Do not lock body scroll here; parent overlays already manage it to avoid conflicts.

  // Move focus into the dialog on open and restore focus on close.
  // This helps screen readers and keyboard users.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocusedElement =
      document.activeElement as HTMLElement | null;
    const node = containerRef.current;

    const focusFirstInteractive = () => {
      if (!node) return;
      const focusableSelectors = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ];
      const focusable = Array.from(
        node.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      if (focusable.length > 0) {
        focusable[0].focus({ preventScroll: true });
        return;
      }
      node?.focus({ preventScroll: true });
    };

    // Timeout allows the dialog to finish mounting/animating before focusing.
    const t = window.setTimeout(focusFirstInteractive, 0);
    return () => {
      window.clearTimeout(t);
      previouslyFocusedElement?.focus?.();
    };
  }, [isOpen]);

  const handleKeyDownWithin = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const node = containerRef.current;
    if (!node) return;
    const focusableSelectors = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ];
    const focusable = Array.from(
      node.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
    ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);

    if (focusable.length === 0) {
      event.preventDefault();
      node.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    }
  };

  return (
    <SettingsSubpageContext.Provider value={{ backTo }}>
      <AnimatePresence mode='wait'>
        {isOpen && (
          <motion.div
            key='settings-overlay'
            initial={animation.initial}
            animate={animation.animate}
            exit={animation.exit}
            transition={{
              duration: 0.28,
              ease: [0.4, 0, 0.2, 1],
              opacity: { duration: 0.28 },
            }}
            ref={containerRef}
            role='dialog'
            aria-modal='true'
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={handleKeyDownWithin}
            onMouseDown={(e) => {
              // Track where the mousedown started
              mouseDownTargetRef.current = e.target;
            }}
            onPointerDown={(e) => {
              // Track where the pointerdown started (for touch/pen)
              mouseDownTargetRef.current = e.target;
            }}
            onClick={(e) => {
              // Only close if both mousedown and click happened on the backdrop
              // This prevents closing when user starts text selection inside dialog
              // and releases outside (which would trigger click on backdrop)
              if (
                e.target === e.currentTarget &&
                mouseDownTargetRef.current === e.currentTarget
              ) {
                onClose();
              }
              // Reset for next interaction
              mouseDownTargetRef.current = null;
            }}
            className='absolute inset-0 z-50 overflow-y-auto overflow-x-clip rounded-2xl bg-surface-container-low px-5 py-0 will-change-transform lg:px-10'
            style={{ transform: 'translateZ(0)' }}
          >
            <AnimatePresence mode='wait' initial={false}>
              <motion.div
                key={contentKey ?? title}
                initial={pageVariants[direction].initial}
                animate={pageVariants[direction].animate}
                exit={pageVariants[direction].exit}
                transition={{
                  duration: 0.28,
                  ease: [0.4, 0, 0.2, 1],
                  opacity: { duration: 0.28 },
                }}
                className='relative flex h-full flex-col'
              >
                <div className='sticky top-0 z-50 mb-3 grid grid-cols-[45px_minmax(0,_1fr)_45px] items-center gap-4 bg-[rgba(242,246,245,0.85)] pb-1 pt-5 backdrop-blur-md lg:pt-7'>
                  <IconButton
                    icon={faArrowLeft as IconDefinition}
                    onClick={backTo}
                    ariaLabel='Back'
                  />
                  <h1 id={titleId} className='text-lg font-medium'>
                    {title}
                  </h1>{' '}
                  <IconButton
                    type='close'
                    onClick={onClose}
                    ariaLabel='Close popup'
                  />
                </div>
                <div className='flex flex-1 flex-col p-1 pb-5'>
                  {children ?? form}
                </div>

                {(footer || action) && (
                  <div
                    role='region'
                    aria-label='Actions'
                    className='sticky bottom-0 mt-2 bg-[rgba(242,246,245,0.85)] pb-3 backdrop-blur-md'
                  >
                    <div className='flex items-center gap-2 px-1 py-3'>
                      {footer ?? (
                        <ActionButton
                          type={action?.type ?? 'button'}
                          intent={action?.intent ?? 'primary'}
                          disabled={action?.disabled}
                          loading={action?.loading}
                          onClick={action?.onClick}
                          form={action?.type === 'submit' ? formId : undefined}
                        >
                          {action?.label}
                        </ActionButton>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </SettingsSubpageContext.Provider>
  );
};
